import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sendApiRequest } from '../utils';
import { toast } from 'react-toastify';
import { useNavigate } from "react-router-dom";

const WithdrawalRequestForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(state => state.user_info[0]?.user);
  const [isLoading, setIsLoading] = useState(false);
  const [bankDetails, setBankDetails] = useState(null);
  const [availableAmount, setAvailableAmount] = useState(0);
  const [isReportClosed, setIsReportClosed] = useState(false);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [deductions, setDeductions] = useState({
    adminFee: 0,
    tds: 0,
    netAmount: 0
  });

  // Fetch available amount, check report status, and check for pending requests on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch available amount
        const statsResponse = await sendApiRequest('api/overview/stats', {}, 'GET');
        if (statsResponse.status === 200) {
          setAvailableAmount(statsResponse.data.data.walletStats.totalIncome);
        }

        // Check if current month's report is closed
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() ;

        const reportResponse = await sendApiRequest(
          `api/packages/admin/closing-report?year=${currentYear}&month=${currentMonth}`,
          {},
          'GET'
        );

        if (reportResponse.status === 200) {
          setIsReportClosed(reportResponse.data.data.isClosed);
        }

        // Check for pending withdrawal requests
        const pendingRequestResponse = await sendApiRequest(
          'api/withdrawal-requests/user',
          { status: 'pending' },
          'GET'
        );

        if (pendingRequestResponse.status === 200 && pendingRequestResponse.data.withdrawalRequests.length > 0) {
          setHasPendingRequest(true);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to fetch required data');
      }
    };

    fetchData();
  }, []);

  // State for form fields and success message
  const [formData, setFormData] = useState({
    withdrawalType: 'BANK (Minimum allowed Rs. 1000)',
    withdrawalAmount: '',
    date: '',
    accountNumber: user?.bank_account_number || '',
    bankName: user?.bank_name || '',
    branchName: user?.branch_name || '',
    ifscCode: user?.ifsc_code || '',
    accountType: user?.account_type || 'Saving Account',
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Set current date on mount
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0].replace(/-/g, '-');
    setFormData((prev) => ({ ...prev, date: today }));
  }, []);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'withdrawalAmount') {
      validateAmount(value);
    }
  };

  // Validate withdrawal amount
  const validateAmount = (amount) => {
    const numAmount = parseFloat(amount) || 0;
    if (numAmount < 1000 && numAmount > 0) {
      setError('Withdrawal amount must be at least ₹1000.');
    } else if (numAmount > availableAmount) {
      setError('Withdrawal amount exceeds available balance.');
    } else {
      setError('');
      // Calculate deductions
      const adminFee = numAmount * 0.05;
      const tds = numAmount * 0.05;
      const netAmount = numAmount - adminFee - tds;
      
      setDeductions({
        adminFee,
        tds,
        netAmount
      });
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isReportClosed) {
      toast.error('Cannot submit withdrawal request. Current month\'s report is not closed yet.');
      return;
    }

    if (hasPendingRequest) {
      toast.error('You already have a pending withdrawal request. Please wait for it to be processed.');
      return;
    }

    if (!formData.withdrawalAmount || parseFloat(formData.withdrawalAmount) < 1000) {
      setError('Please enter a valid withdrawal amount of at least ₹1000.');
      return;
    }
    if (error) return;

    setIsLoading(true);
    try {
      const requestPayload = {
        amount: parseFloat(formData.withdrawalAmount),
        bankDetails: {
          accountNumber: formData.accountNumber,
          bankName: formData.bankName,
          branchName: formData.branchName,
          ifscCode: formData.ifscCode,
          accountType: formData.accountType
        }
      };

      const response = await sendApiRequest('api/withdrawal-requests', requestPayload, 'POST');

      if (response.data?.success) {
        toast.success('Withdrawal request submitted successfully!');
        setFormData((prev) => ({ ...prev, withdrawalAmount: '' }));
        navigate('/dashboard');
        setError('');
        // Refresh available amount after successful withdrawal request
        const statsResponse = await sendApiRequest('api/overview/stats', {}, 'GET');
        if (statsResponse.status === 200) {
          setAvailableAmount(statsResponse.data.data.walletStats.totalIncome);
        }
      } else {
        const errorMessage = response.data?.message || 'Failed to submit withdrawal request';
        toast.error(errorMessage);
        setError(errorMessage);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred while submitting the request';
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 p-6 font-sans relative overflow-hidden">
      {/* Animated Background Particles */}
      <div className="absolute inset-0 opacity-20">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-blue-400 rounded-full animate-float"
            style={{
              width: `${Math.random() * 10 + 5}px`,
              height: `${Math.random() * 10 + 5}px`,
              left: `${Math.random() * 100}vw`,
              top: `${Math.random() * 100}vh`,
              animationDelay: `${Math.random() * 10}s`,
            }}
          />
        ))}
      </div>

      <div className="max-w-2xl mx-auto relative z-10">
        {/* Header */}
        <h2 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-indigo-600 mb-8 animate-pulse">
          Withdrawal Request Form
        </h2>

        {/* Report Status Alert */}
        {!isReportClosed && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <p className="font-semibold">⚠️ Withdrawal Request Not Available</p>
            <p>Withdrawal requests can only be submitted after the current month's report is closed.</p>
          </div>
        )}

        {hasPendingRequest && (
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative mb-4">
            You already have a pending withdrawal request. Please wait for it to be processed before making a new request.
          </div>
        )}

        {/* Form Card */}
        <div className={`bg-white rounded-xl shadow-lg p-8 ${!isReportClosed ? 'opacity-50' : ''}`}>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Withdrawal Type */}
            <div className="flex flex-col">
              <label className="text-gray-800 font-semibold mb-2">Withdrawal Type</label>
              <select
                name="withdrawalType"
                value={formData.withdrawalType}
                onChange={handleChange}
                className="p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-300/50 focus:border-transparent transition duration-300 text-gray-800 hover:bg-gray-100"
                disabled
              >
                <option>BANK (Minimum allowed Rs. 1000)</option>
              </select>
            </div>

            {/* Available Amount */}
            <div className="flex flex-col">
              <label className="text-gray-800 font-semibold mb-2">Available Amount:</label>
              <input
                type="text"
                value={`₹${availableAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`}
                readOnly
                className="p-3 bg-gray-100 border border-gray-200 rounded-xl text-gray-600 cursor-not-allowed"
              />
            </div>

            {/* Withdrawal Amount */}
            <div className="flex flex-col">
              <label className="text-gray-800 font-semibold mb-2">Withdrawal Amount:</label>
              <input
                type="number"
                name="withdrawalAmount"
                value={formData.withdrawalAmount}
                onChange={handleChange}
                placeholder="Enter amount (₹)"
                className="p-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-300/50 focus:border-transparent transition duration-300 text-gray-800 hover:bg-gray-50"
                required
              />
              {error && <p className="text-red-500 text-sm font-medium mt-1 animate-fade-in">{error}</p>}
            </div>

            {/* Date */}
            <div className="flex flex-col">
              <label className="text-gray-800 font-semibold mb-2">Date:</label>
              <input
                type="text"
                name="date"
                value={formData.date}
                readOnly
                className="p-3 bg-gray-100 border border-gray-200 rounded-xl text-gray-600 cursor-not-allowed"
              />
            </div>

            {/* Account Number */}
            <div className="flex flex-col">
              <label className="text-gray-800 font-semibold mb-2">Account Number:</label>
              <input
                type="text"
                name="accountNumber"
                value={formData.accountNumber}
                readOnly
                className="p-3 bg-gray-100 border border-gray-200 rounded-xl text-gray-600 cursor-not-allowed"
              />
            </div>

            {/* Bank Name */}
            <div className="flex flex-col">
              <label className="text-gray-800 font-semibold mb-2">Bank Name:</label>
              <input
                type="text"
                name="bankName"
                value={formData.bankName}
                readOnly
                className="p-3 bg-gray-100 border border-gray-200 rounded-xl text-gray-600 cursor-not-allowed"
              />
            </div>

            {/* Branch Name */}
            <div className="flex flex-col">
              <label className="text-gray-800 font-semibold mb-2">Branch Name:</label>
              <input
                type="text"
                name="branchName"
                value={formData.branchName}
                readOnly
                className="p-3 bg-gray-100 border border-gray-200 rounded-xl text-gray-600 cursor-not-allowed"
              />
            </div>

            {/* IFSC Code */}
            <div className="flex flex-col">
              <label className="text-gray-800 font-semibold mb-2">IFSC Code:</label>
              <input
                type="text"
                name="ifscCode"
                value={formData.ifscCode}
                readOnly
                className="p-3 bg-gray-100 border border-gray-200 rounded-xl text-gray-600 cursor-not-allowed"
              />
            </div>

            {/* Account Type */}
            <div className="flex flex-col">
              <label className="text-gray-800 font-semibold mb-2">Account Type:</label>
              <select
                name="accountType"
                value={formData.accountType}
                onChange={handleChange}
                className="p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-300/50 focus:border-transparent transition duration-300 text-gray-800 hover:bg-gray-100"
                disabled
              >
                <option>Saving Account</option>
              </select>
            </div>

            {/* Note */}
            <div className="flex flex-col">
              <p className="text-red-500 text-sm font-medium mt-1">
                Note: To update account details go to Update Profile page.
              </p>
            </div>

            {/* Deductions Section */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Deductions</h3>
              
              {/* Admin Fee */}
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600">Admin Fee (5%)</span>
                <span className="font-medium">₹{deductions.adminFee.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
              </div>

              {/* TDS */}
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600">TDS (5%)</span>
                <span className="font-medium">₹{deductions.tds.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
              </div>

              {/* Net Amount */}
              <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                <span className="text-gray-800 font-semibold">Net Amount</span>
                <span className="text-green-600 font-bold">
                  ₹{deductions.netAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isLoading || !isReportClosed  || hasPendingRequest}
                className={`bg-gradient-to-r from-blue-500 to-teal-500 text-white p-3 rounded-xl hover:scale-105 hover:shadow-lg transition-all duration-300 focus:ring-2 focus:ring-blue-300/50 ${
                  (isLoading || !isReportClosed || hasPendingRequest) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
              {/* {hasPendingRequest?"":<>{isLoading ? 'Submitting...' : !isReportClosed ? 'Report Not Closed' : 'SUBMIT FORM'}</>} */}
                {isLoading ? 'Submitting...' : !isReportClosed ? 'Report Not Closed' : 'SUBMIT FORM'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* CSS for Animations */}
      <style jsx>{`
        @keyframes float {
          0% {
            transform: translateY(0) scale(1);
            opacity: 0.5;
          }
          50% {
            transform: translateY(-20px) scale(1.2);
            opacity: 0.8;
          }
          100% {
            transform: translateY(0) scale(1);
            opacity: 0.5;
          }
        }

        .animate-float {
          animation: float 6s infinite ease-in-out;
        }

        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-5px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default WithdrawalRequestForm;
